'use client';

import { useState, useEffect } from 'react';
import { FirebaseClientProvider } from '@/firebase/client-provider';
import { LicenseProvider } from '@/context/LicenseContext';
import { ThemeProvider } from '@/context/ThemeContext';
import dynamic from 'next/dynamic';
import { PerfLogger } from '@/components/perf/PerfLogger';

const PasswordUpdateEnforcement = dynamic(
  () => import('@/components/PasswordUpdateEnforcement'),
  { ssr: false }
);
const ServiceWorkerRegistration = dynamic(
  () => import('@/components/ServiceWorkerRegistration').then((mod) => ({
    default: mod.ServiceWorkerRegistration,
  })),
  { ssr: false }
);
const ProjectSearchDialog = dynamic(
  () => import('@/components/ProjectSearchDialog').then((mod) => ({
    default: mod.ProjectSearchDialog,
  })),
  { ssr: false }
);

export function Providers({ children }: { children: React.ReactNode }) {
  const [showDeferredFeatures, setShowDeferredFeatures] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Only pull in the dialog's chunk once the shortcut is first used, so restoring the
  // app-wide shortcut doesn't undo the load-time work that removed it.
  const [searchEverOpened, setSearchEverOpened] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowDeferredFeatures(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  // ⌘J / Ctrl+J — jump to another project from anywhere in the app.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
      if (event.key.toLowerCase() !== 'j') return;
      event.preventDefault();
      setSearchEverOpened(true);
      setSearchOpen((open) => !open);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <FirebaseClientProvider>
      <ThemeProvider>
        <LicenseProvider>
          <PerfLogger />
          {showDeferredFeatures && <ServiceWorkerRegistration />}
          {showDeferredFeatures && <PasswordUpdateEnforcement />}
          {children}
          {searchEverOpened && (
            <ProjectSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
          )}
        </LicenseProvider>
      </ThemeProvider>
    </FirebaseClientProvider>
  );
}
